import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Bell, 
  Lock, 
  Globe, 
  Moon, 
  Mail, 
  Shield, 
  Smartphone,
  Save,
  X
} from 'lucide-react';
import Button from '../components/ui/Button';
import { toast } from 'react-hot-toast';

const Settings: React.FC = () => {
  const [settings, setSettings] = useState({
    notifications: {
      email: true,
      push: true,
      mentorMessages: true,
      challengeReminders: true,
      scholarshipDeadlines: true,
      weeklyDigest: true
    },
    privacy: {
      profileVisibility: 'public',
      showEmail: false,
      showPhone: false,
      allowMessaging: true
    },
    appearance: {
      theme: 'light',
      fontSize: 'medium',
      reducedMotion: false
    },
    language: 'en',
    twoFactorEnabled: false
  });

  const [loading, setLoading] = useState(false);

  const handleToggle = (category: string, setting: string) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [setting]: !prev[category as keyof typeof prev][setting as keyof typeof prev[keyof typeof prev]]
      }
    }));
  };

  const handleSelect = (category: string, setting: string, value: string) => {
    setSettings(prev => ({
      ...prev,
      [category]: {
        ...prev[category as keyof typeof prev],
        [setting]: value
      }
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-8"
      >
        <div className="flex justify-between items-center">
          <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
          <Button
            onClick={handleSave}
            isLoading={loading}
            icon={<Save className="h-4 w-4" />}
          >
            Save Changes
          </Button>
        </div>

        {/* Notifications */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Bell className="h-5 w-5 text-primary-600 mr-2" />
            Notifications
          </h2>
          <div className="space-y-4">
            {Object.entries(settings.notifications).map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <label className="flex items-center cursor-pointer">
                  <span className="text-gray-700 capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </span>
                </label>
                <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                  <input
                    type="checkbox"
                    className="opacity-0 w-0 h-0"
                    checked={value}
                    onChange={() => handleToggle('notifications', key)}
                  />
                  <span className={`absolute cursor-pointer inset-0 rounded-full ${
                    value ? 'bg-primary-600' : 'bg-gray-300'
                  }`}>
                    <span className={`absolute inset-y-0 left-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-200 ${
                      value ? 'translate-x-6' : 'translate-x-0'
                    }`} />
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Privacy */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Shield className="h-5 w-5 text-primary-600 mr-2" />
            Privacy
          </h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Profile Visibility
              </label>
              <select
                value={settings.privacy.profileVisibility}
                onChange={(e) => handleSelect('privacy', 'profileVisibility', e.target.value)}
                className="select"
              >
                <option value="public">Public</option>
                <option value="private">Private</option>
                <option value="connections">Connections Only</option>
              </select>
            </div>
            {Object.entries(settings.privacy)
              .filter(([key]) => key !== 'profileVisibility')
              .map(([key, value]) => (
                <div key={key} className="flex items-center justify-between">
                  <label className="flex items-center cursor-pointer">
                    <span className="text-gray-700 capitalize">
                      {key.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </label>
                  <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                    <input
                      type="checkbox"
                      className="opacity-0 w-0 h-0"
                      checked={value}
                      onChange={() => handleToggle('privacy', key)}
                    />
                    <span className={`absolute cursor-pointer inset-0 rounded-full ${
                      value ? 'bg-primary-600' : 'bg-gray-300'
                    }`}>
                      <span className={`absolute inset-y-0 left-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-200 ${
                        value ? 'translate-x-6' : 'translate-x-0'
                      }`} />
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </section>

        {/* Security */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Lock className="h-5 w-5 text-primary-600 mr-2" />
            Security
          </h2>
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-gray-900 font-medium">Two-Factor Authentication</h3>
                <p className="text-sm text-gray-500">Add an extra layer of security to your account</p>
              </div>
              <Button
                variant={settings.twoFactorEnabled ? 'outline' : 'primary'}
                onClick={() => handleToggle('twoFactorEnabled', '')}
              >
                {settings.twoFactorEnabled ? 'Disable' : 'Enable'}
              </Button>
            </div>
          </div>
        </section>

        {/* Preferences */}
        <section className="bg-white rounded-xl shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
            <Globe className="h-5 w-5 text-primary-600 mr-2" />
            Preferences
          </h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Language
              </label>
              <select
                value={settings.language}
                onChange={(e) => handleSelect('language', '', e.target.value)}
                className="select"
              >
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Theme
              </label>
              <select
                value={settings.appearance.theme}
                onChange={(e) => handleSelect('appearance', 'theme', e.target.value)}
                className="select"
              >
                <option value="light">Light</option>
                <option value="dark">Dark</option>
                <option value="system">System</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Font Size
              </label>
              <select
                value={settings.appearance.fontSize}
                onChange={(e) => handleSelect('appearance', 'fontSize', e.target.value)}
                className="select"
              >
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-gray-900 font-medium">Reduced Motion</h3>
                <p className="text-sm text-gray-500">Minimize animations and transitions</p>
              </div>
              <div className="relative inline-block w-12 h-6 transition duration-200 ease-in-out">
                <input
                  type="checkbox"
                  className="opacity-0 w-0 h-0"
                  checked={settings.appearance.reducedMotion}
                  onChange={() => handleToggle('appearance', 'reducedMotion')}
                />
                <span className={`absolute cursor-pointer inset-0 rounded-full ${
                  settings.appearance.reducedMotion ? 'bg-primary-600' : 'bg-gray-300'
                }`}>
                  <span className={`absolute inset-y-0 left-0 w-6 h-6 bg-white rounded-full shadow transform transition-transform duration-200 ${
                    settings.appearance.reducedMotion ? 'translate-x-6' : 'translate-x-0'
                  }`} />
                </span>
              </div>
            </div>
          </div>
        </section>
      </motion.div>
    </div>
  );
};

export default Settings;